import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Row, Col, InputGroup, Form, Pagination } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { listarRamos, excluirRamo } from '~/Services/Service';
import ModalRamo from './ModalRamo';

const ITENS_POR_PAGINA = 20;

const formatarObra = (obra) => {
  if (obra === 'S') return 'Sim';
  if (obra === 'N') return 'Não';
  return '—';
};

const RamosAtividade = () => {
  const [ramos, setRamos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [confirmacao, setConfirmacao] = useState({ show: false, item: null });
  const [filtro, setFiltro] = useState('');
  const [ordenacao, setOrdenacao] = useState({ campo: 'descricao', direcao: 'asc' });
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    carregarRamos();
  }, []);

  const carregarRamos = async () => {
    setLoading(true);
    try {
      const resp = await listarRamos();
      setRamos(resp || []);
    } catch (e) {
      toast.error('Erro ao carregar ramos de atividade.');
    } finally {
      setLoading(false);
    }
  };

  const handleOrdenacao = (campo) => {
    setOrdenacao((prev) => {
      if (prev.campo === campo) {
        return { campo: campo, direcao: prev.direcao === 'asc' ? 'desc' : 'asc' };
      }
      return { campo: campo, direcao: 'asc' };
    });
    setPagina(1);
  };

  const iconeOrdenacao = (campo) => {
    if (ordenacao.campo !== campo) return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    return ordenacao.direcao === 'asc'
      ? <FaSortUp style={{ marginLeft: 4 }} />
      : <FaSortDown style={{ marginLeft: 4 }} />;
  };

  const thSortStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

  const handleNovo = () => {
    setItemSelecionado(null);
    setShowModal(true);
  };

  const handleEditar = (item) => {
    setItemSelecionado(item);
    setShowModal(true);
  };

  const handleExcluir = (item) => {
    setConfirmacao({ show: true, item: item });
  };

  const handleConfirmarExclusao = async (confirmado) => {
    if (!confirmado) {
      setConfirmacao({ show: false, item: null });
      return;
    }
    const item = confirmacao.item;
    setConfirmacao({ show: false, item: null });
    try {
      await excluirRamo({ codigo: item.codigo });
      toast.success('Ramo excluído com sucesso.');
      carregarRamos();
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao excluir ramo.');
    }
  };

  const handleSalvar = () => {
    setShowModal(false);
    carregarRamos();
  };

  const handleFiltro = (e) => {
    setFiltro(e.target.value);
    setPagina(1);
  };

  const ramosFiltrados = ramos.filter((item) => {
    if (!filtro) return true;
    const termo = filtro.toLowerCase();
    return (item.descricao || '').toLowerCase().indexOf(termo) >= 0;
  });

  const ramosOrdenados = ramosFiltrados.slice().sort((a, b) => {
    const campo = ordenacao.campo;
    const dir = ordenacao.direcao === 'asc' ? 1 : -1;
    let valA = a[campo];
    let valB = b[campo];
    if (valA == null) valA = '';
    if (valB == null) valB = '';
    if (typeof valA === 'string') {
      return dir * valA.toLowerCase().localeCompare(String(valB).toLowerCase());
    }
    return dir * (valA - valB);
  });

  const totalRegistros = ramosOrdenados.length;
  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / ITENS_POR_PAGINA));
  const paginaSegura = Math.min(pagina, totalPaginas);
  const ramosPaginados = ramosOrdenados.slice(
    (paginaSegura - 1) * ITENS_POR_PAGINA,
    paginaSegura * ITENS_POR_PAGINA
  );

  const mudarPagina = (novaPagina) => {
    if (novaPagina < 1 || novaPagina > totalPaginas) return;
    setPagina(novaPagina);
  };

  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;

    const maxBotoes = 5;
    const itens = [];

    let inicioPaginas = Math.max(1, paginaSegura - Math.floor(maxBotoes / 2));
    let fimPaginas = Math.min(totalPaginas, inicioPaginas + maxBotoes - 1);
    if (fimPaginas - inicioPaginas < maxBotoes - 1) {
      inicioPaginas = Math.max(1, fimPaginas - maxBotoes + 1);
    }

    itens.push(
      <Pagination.First key="first" onClick={() => { mudarPagina(1); }} disabled={paginaSegura === 1} />
    );
    itens.push(
      <Pagination.Prev key="prev" onClick={() => { mudarPagina(paginaSegura - 1); }} disabled={paginaSegura === 1} />
    );

    if (inicioPaginas > 1) {
      itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }

    for (let n = inicioPaginas; n <= fimPaginas; n++) {
      const num = n;
      itens.push(
        <Pagination.Item key={num} active={num === paginaSegura} onClick={() => { mudarPagina(num); }}>
          {num}
        </Pagination.Item>
      );
    }

    if (fimPaginas < totalPaginas) {
      itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }

    itens.push(
      <Pagination.Next key="next" onClick={() => { mudarPagina(paginaSegura + 1); }} disabled={paginaSegura === totalPaginas} />
    );
    itens.push(
      <Pagination.Last key="last" onClick={() => { mudarPagina(totalPaginas); }} disabled={paginaSegura === totalPaginas} />
    );

    return (
      <div className="d-flex justify-content-between align-items-center px-3 py-2">
        <small className="text-muted">
          Mostrando {ramosPaginados.length > 0 ? (paginaSegura - 1) * ITENS_POR_PAGINA + 1 : 0} até{' '}
          {Math.min(paginaSegura * ITENS_POR_PAGINA, totalRegistros)} de {totalRegistros} registros
        </small>
        <Pagination size="sm" className="mb-0">{itens}</Pagination>
      </div>
    );
  };

  const itemExclusao = confirmacao.item;

  if (loading) return <Loading />;

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Ramos de Atividade" />

      <Card className="shadow-sm mb-3" style={{ maxWidth: '700px', margin: '0 auto' }}>
        <Card.Body className="py-2">
          <Row className="align-items-center">
            <Col md={4}>
              <InputGroup size="sm">
                <InputGroup.Prepend>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  placeholder="Filtrar por descrição..."
                  value={filtro}
                  onChange={handleFiltro}
                />
              </InputGroup>
            </Col>
            <Col className="text-right">
              <Button size="sm" variant="primary" onClick={handleNovo}>
                <FaPlus className="mr-1" /> Novo Ramo
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm" style={{ maxWidth: '700px', margin: '0 auto' }}>
        <Card.Header
          className="d-flex justify-content-between align-items-center py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>
            Ramos de Atividade
            <span className="ml-2" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
              ({totalRegistros} {totalRegistros === 1 ? 'registro' : 'registros'})
            </span>
          </strong>
        </Card.Header>
        <Card.Body className="p-0">
          {totalRegistros === 0 ? (
            <p className="text-muted text-center my-4">Nenhum ramo cadastrado.</p>
          ) : (
            <>
              <div style={{ maxHeight: '25rem', overflowY: 'auto' }}>
              <Table striped bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th
                      style={Object.assign({ width: '100px', position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff' }, thSortStyle)}
                      onClick={() => { handleOrdenacao('codigo'); }}
                    >
                      Código{iconeOrdenacao('codigo')}
                    </th>
                    <th
                      style={Object.assign({ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff' }, thSortStyle)}
                      onClick={() => { handleOrdenacao('descricao'); }}
                    >
                      Descrição{iconeOrdenacao('descricao')}
                    </th>
                    <th
                      style={Object.assign({ width: '100px', position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff' }, thSortStyle)}
                      onClick={() => { handleOrdenacao('obra'); }}
                    >
                      Obra{iconeOrdenacao('obra')}
                    </th>
                    <th style={{ width: '90px', textAlign: 'center', position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {ramosPaginados.map((r) => {
                    return (
                      <tr key={r.codigo}>
                        <td>{r.codigo}</td>
                        <td>{r.descricao}</td>
                        <td>{formatarObra(r.obra)}</td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                          <Button
                            size="sm"
                            variant="outline-warning"
                            title="Editar"
                            className="mr-1"
                            onClick={() => { handleEditar(r); }}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            title="Excluir"
                            onClick={() => { handleExcluir(r); }}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              </div>
              {renderPaginacao()}
            </>
          )}
        </Card.Body>
      </Card>

      <ModalConfirmacao
        show={confirmacao.show}
        title="Confirmar Exclusão"
        message={
          itemExclusao
            ? 'Deseja realmente excluir o ramo "' + (itemExclusao.descricao || '') + '"?'
            : ''
        }
        onClose={handleConfirmarExclusao}
      />

      <ModalRamo
        show={showModal}
        onHide={() => { setShowModal(false); }}
        ramo={itemSelecionado}
        onSalvar={handleSalvar}
      />
    </Container>
  );
};

export default RamosAtividade;
